// The six code boxes (design/kit/components/CodeInput) as plain functions, so typing, paste,
// Backspace and the arrow keys are tested without a browser.

export const CODE_LENGTH = 6;

export type Boxes = readonly string[];
export type BoxChange = { boxes: string[]; focus: number };

export const emptyBoxes = (): string[] => Array.from({ length: CODE_LENGTH }, () => '');

const last = CODE_LENGTH - 1;
const digitsOf = (text: string) => text.replace(/\D/g, '');

/**
 * A box's new value: one typed digit, or several from a paste or the phone's code autofill,
 * which fill this box and the ones after it.
 */
export function typeInto(boxes: Boxes, index: number, value: string): BoxChange {
  const next = [...boxes];
  let digits = digitsOf(value);
  const old = boxes[index];
  // Typing into a full box gives "old + new" (or "new + old"): keep the new digit.
  if (old && digits.length === 2) {
    if (digits.startsWith(old)) digits = digits.slice(1);
    else if (digits.endsWith(old)) digits = digits.slice(0, 1);
  }
  if (!digits) {
    next[index] = '';
    return { boxes: next, focus: index };
  }
  // A whole code pasted into any box fills all six.
  const start = digits.length >= CODE_LENGTH ? 0 : index;
  const chars = digits.slice(0, CODE_LENGTH - start).split('');
  chars.forEach((d, i) => {
    next[start + i] = d;
  });
  return { boxes: next, focus: Math.min(start + chars.length, last) };
}

/** Backspace clears this box, or the one before when this one is already empty. */
export function backspace(boxes: Boxes, index: number): BoxChange {
  const next = [...boxes];
  if (next[index]) {
    next[index] = '';
    return { boxes: next, focus: index };
  }
  const prev = Math.max(index - 1, 0);
  next[prev] = '';
  return { boxes: next, focus: prev };
}

export function moveFocus(index: number, key: 'ArrowLeft' | 'ArrowRight' | 'Home' | 'End') {
  if (key === 'Home') return 0;
  if (key === 'End') return last;
  return key === 'ArrowLeft' ? Math.max(index - 1, 0) : Math.min(index + 1, last);
}

export const codeOf = (boxes: Boxes) => boxes.join('');
export const isComplete = (boxes: Boxes) => boxes.every((d) => /^\d$/.test(d));

/** "Resend code in 0:30", then "Resend code". */
export function resendLabel(secondsLeft: number): string {
  if (secondsLeft <= 0) return 'Resend code';
  const m = Math.floor(secondsLeft / 60);
  const s = String(secondsLeft % 60).padStart(2, '0');
  return `Resend code in ${m}:${s}`;
}
